<template>
  <a-modal
    v-model="modalVisible"
    :title="
      logworkId
        ? $t('logwork.modal.edit_title') || 'Edit Logwork'
        : $t('logwork.modal.title')
    "
    :width="500"
    :maskClosable="false"
    :zIndex="2000"
    @ok="handleSubmit"
    @cancel="handleCancel"
    :okText="
      logworkId
        ? $t('logwork.modal.update') || 'Update'
        : $t('logwork.modal.log_work')
    "
    :cancelText="$t('common.cancel')"
  >
    <a-spin :spinning="loading">
      <a-form :form="form" layout="vertical" class="getBacklogwork-form">
        <!-- Ngày log -->
        <a-form-item>
          <template slot="label">
            <span>
              {{ $t("logwork.modal.date") }}
              <span class="required-star">*</span>
            </span>
          </template>
          <a-date-picker
            v-decorator="['date', { initialValue: formData.date, rules: dateRules }]"
            :disabledDate="disableFutureDates"
            :format="dateFormat"
            :placeholder="$t('logwork.modal.select_date')"
            :getCalendarContainer="trigger => trigger.parentNode"
            style="width: 100%"
            @change="onDateChange"
          />
        </a-form-item>

        <!-- Dự án -->
        <a-form-item>
          <template slot="label">
            <span>
              {{ $t("logwork.modal.project") }}
              <span class="required-star">*</span>
            </span>
          </template>
          <a-select
            v-decorator="['projectId', { initialValue: formData.projectId, rules: projectRules }]"
            :options="projectOptions"
            :placeholder="$t('logwork.modal.select_project')"
            :dropdownStyle="{ zIndex: 3000 }"
            @change="handleProjectChange"
          />
        </a-form-item>

        <!-- Issue ID -->
        <a-form-item>
          <template slot="label">
            <span>
              {{ $t("logwork.modal.issue_id") }}
            </span>
          </template>
          <a-input-search
            v-decorator="['issueId', { initialValue: formData.issueId }]"
            :placeholder="$t('logwork.modal.select_issue')"
            :loading="loadingIssues"
            @search="handleIssueSearch"
            @change="onIssueInput"
            @paste="onIssuePaste"
            enter-button
          />
          <div v-if="issueSummary" class="issue-summary">
            {{ issueSummary }}
          </div>
        </a-form-item>

        <!-- Thuộc tính -->
        <a-form-item>
          <template slot="label">
            <span>
              {{ $t("logwork.modal.attribute") }}
            </span>
          </template>
          <a-select
            v-decorator="['attributeId', { initialValue: formData.attributeId }]"
            :options="attributeOptions"
            :placeholder="$t('logwork.modal.select_attribute')"
            :dropdownStyle="{ zIndex: 3000 }"
            @change="onAttributeChange"
          />
        </a-form-item>

        <!-- Thời gian -->
        <a-form-item>
          <template slot="label">
            <span>
              {{ $t("logwork.modal.spent_time") }}
              <span class="required-star">*</span>
            </span>
          </template>
          <div class="time-input-group">
            <a-input
              v-decorator="['spentTime', { initialValue: formData.spentTime, rules: spentTimeRules }]"
              :placeholder="$t('logwork.modal.time_format_help')"
              style="width: 100%"
              @change="onSpentTimeChange"
            />
          </div>
        </a-form-item>

        <!-- Mô tả -->
        <a-form-item>
          <template slot="label">
            <span>
              {{ $t("logwork.modal.description") }}
              <span class="required-star">*</span>
            </span>
          </template>
          <a-textarea
            v-decorator="['note', { initialValue: formData.note, rules: noteRules }]"
            :rows="3"
            :placeholder="$t('logwork.modal.description_placeholder')"
            @change="onNoteChange"
          />
        </a-form-item>

        <!-- Log another checkbox -->
        <a-form-item v-if="!logworkId">
          <a-checkbox v-model="logAnother">
            {{ $t("logwork.modal.log_another") }}
          </a-checkbox>
        </a-form-item>
      </a-form>
    </a-spin>
  </a-modal>
</template>

<script>
import moment from "moment";
import debounce from "lodash/debounce";
import LogworkService from "../../api/logwork";
import { message } from 'ant-design-vue';

const dateFormat = "DD/MM/YYYY";

const service = LogworkService;

export default {
  name: "LogworkModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    logworkId: {
      type: [Number, String],
      default: null
    },
    defaultProjectId: {
      type: [Number, String],
      default: null
    },
    employeeId: {
      type: [Number, String],
      default: null
    },
    defaultDate: {
      type: [String, Object],
      default: null
    }
  },
  data() {
    return {
      dateFormat,
      form: this.$form.createForm(this),
      loading: false,
      loadingIssues: false,
      logAnother: true,
      modalVisible: false,
      formData: {
        date: moment(),
        projectId: null,
        issueId: null,
        attributeId: null,
        spentTime: null,
        note: ""
      },
      projects: [],
      issueSummary: "",
      attributes: []
    };
  },
  computed: {
    dateRules() {
      const vm = this;
      return [
        {
          validator(rule, value, callback) {
            if (value) {
              callback();
            } else {
              callback(new Error(vm.$t("logwork.modal.date_required")));
            }
          }
        }
      ];
    },
    projectRules() {
      const vm = this;
      return [
        {
          validator(rule, value, callback) {
            if (value != null && value !== "") {
              callback();
            } else {
              callback(new Error(vm.$t("logwork.modal.project_required")));
            }
          }
        }
      ];
    },
    spentTimeRules() {
      const vm = this;
      return [
        {
          validator(rule, value, callback) {
            const v = value != null ? String(value).trim() : "";
            if (!v) {
              callback(new Error(vm.$t("logwork.modal.time_required")));
              return;
            }
            const num = parseFloat(v);
            if (isNaN(num) || num <= 0) {
              callback(new Error(vm.$t("logwork.modal.time_required")));
              return;
            }
            callback();
          }
        }
      ];
    },
    noteRules() {
      const vm = this;
      return [
        {
          validator(rule, value, callback) {
            const v = value != null ? String(value).trim() : "";
            if (v) {
              callback();
            } else {
              callback(new Error(vm.$t("logwork.modal.note")));
            }
          }
        }
      ];
    },
    projectOptions() {
      return this.projects.map(p => ({
        label: p.name,
        value: p.id,
        key: p.key // Assuming project object has a key
      }));
    },
    attributeOptions() {
      return this.attributes.map(a => ({
        label: a.name,
        value: a.id
      }));
    }
  },
  watch: {
    visible(newVal) {
      this.modalVisible = newVal;
      if (newVal) {
        this.resetForm();

        this.fetchProjects();
        this.fetchAttributes();
        this.$nextTick(() => {
          if (this.logworkId) {
            this.fetchLogworkDetail(this.logworkId);
          } else if (this.defaultDate) {
            const m = moment(this.defaultDate);
            if (m.isValid()) {
              this.formData.date = m;
              this.form.setFieldsValue({ date: m });
            }
          }
        });
      }
    },
    modalVisible(newVal) {
      if (!newVal) {
        this.$emit("update:visible", false);
      }
    }
  },
  created() {
    message.config({ zIndex: 3000 });
    this.searchOnIdle = debounce(this.searchOnIdleFn, 3000);
  },
  methods: {
    onIssueInput() {
      this.searchOnIdle();
    },
    onIssuePaste(e) {
      const text = (e.clipboardData || window.clipboardData).getData("text/plain");
      const trimmed = text ? String(text).trim() : "";
      if (trimmed && this.formData.projectId) {
        this.handleIssueSearch(trimmed);
      }
    },
    searchOnIdleFn() {
      const v = this.form.getFieldValue("issueId");
      const trimmed = v ? String(v).trim() : "";
      if (trimmed && this.formData.projectId) {
        this.handleIssueSearch(trimmed);
      }
    },
    onDateChange(date) {
      this.formData.date = date;
    },
    onAttributeChange(val) {
      this.formData.attributeId = val;
    },
    onSpentTimeChange(e) {
      this.formData.spentTime = e.target.value;
    },
    onNoteChange(e) {
      this.formData.note = e.target.value;
    },
    disableFutureDates(current) {
      // Disable selecting dates after today
      return current && current > moment().endOf("day");
    },
    fetchAttributes() {
      service
        .getAttributes()
        .then(response => {
          this.attributes = response.data?.data || [];
        })
        .catch(error => {
          console.error("Failed to fetch attributes:", error);
        });
    },
    fetchProjects() {
      service
        .getProjects()
        .then(response => {
          this.projects = response.data?.data || [];

          // If opening from timeline "create-logwork", prefer defaultProjectId
          const desired =
            !this.logworkId && this.defaultProjectId != null && this.defaultProjectId !== ""
              ? Number(this.defaultProjectId)
              : null;
          const desiredProject =
            desired != null ? this.projects.find(p => Number(p.id) === desired) : null;

          if (desiredProject) {
            this.formData.projectId = desiredProject.id;
            this.form.setFieldsValue({ projectId: desiredProject.id, issueId: undefined });
            this.handleProjectChange(desiredProject.id);
            return;
          }

          // Fallback: auto-select first project if nothing selected yet
          if (this.projects.length > 0 && !this.formData.projectId) {
            this.formData.projectId = this.projects[0].id;
            this.handleProjectChange(this.formData.projectId);
          }
        })
        .catch(error => {
          console.error("Failed to fetch projects:", error);
          this.$message.error(this.$t("logwork.modal.error_projects"));
        });
    },
    handleProjectChange(value) {
      this.form.setFieldsValue({ projectId: value, issueId: undefined });
      this.formData.projectId = value;
      this.formData.issueId = null;
      this.issueSummary = "";
    },
    handleIssueSearch(value) {
      const v = value != null ? String(value).trim() : "";
      if (!v || !this.formData.projectId) return;

      const project = this.projects.find(p => p.id === this.formData.projectId);

      if (!project) return;

      // Use project.key if available, otherwise try to guess or use project name (risky)
      // Ideally the project list API returns the project key.
      // If not, we might need to ask the user to input the full key including project prefix.
      let projectKey = project.key || project.project_key;

      if (!projectKey) {
        // Project key not found, cannot search issue
        this.form.setFields({
          issueId: {
            errors: [new Error(this.$t("logwork.modal.project_key_not_found") || "Project key not found, unable to search issue")]
          }
        });
        return;
      }

      this.loadingIssues = true;
      this.issueSummary = "";
      service
        .getBacklogIssue({
          project_key: projectKey,
          issue_key: v
        })
        .then(response => {
          const data = response.data;

          if (data && data.issue_id) {
            this.issueSummary = data.issue_id + " " +data.title;
            this.form.setFields({ issueId: { errors: [] } });
            this.form.setFieldsValue({ issueId: data.issue_id });

            // Ensure the input matches the returned issue_id (case sensitivity etc)
            this.formData.issueId = data.issue_id;
          } else {
            message.warning(
              this.$t("logwork.modal.issue_not_found") || "Issue not found"
            );
            this.issueSummary = "";
            this.form.setFields({ issueId: { errors: [] } });
          }
        })
        .catch(error => {
          console.error("Failed to search issue:", error);
          this.issueSummary = "";
          this.form.setFields({ issueId: { errors: [] } });
          message.error(
            this.$t("logwork.modal.error_issue_search") ||
              "Failed to search issue"
          );
        })
        .finally(() => {
          this.loadingIssues = false;
        });
    },
    fetchLogworkDetail(id) {
      this.loading = true;
      service
        .getLogworkDetail(id)
        .then(response => {
          const data = response.data;
          if (data) {
            this.formData = {
              date: moment(data.log_date),
              projectId: data.project_id,
              issueId: data.issue_id,
              attributeId: data.log_work_attribute_id,
              spentTime: data.spend_hours,
              note: data.desc
            };
            this.issueSummary = data.issue_summary || "";
            this.$nextTick(() => {
              this.form.setFieldsValue({
                date: moment(data.log_date),
                projectId: data.project_id,
                issueId: data.issue_id,
                attributeId: data.log_work_attribute_id,
                spentTime: data.spend_hours,
                note: data.desc || ""
              });
            });
          }
        })
        .catch(error => {
          console.error("Failed to fetch logwork detail:", error);
          this.$message.error(this.$t("logwork.modal.error_detail"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleSubmit() {
      const fields = ["date", "projectId", "spentTime", "note"];
      this.form.validateFields(fields, err => {
        if (err) return;

        this.loading = true;
        const payload = {
          date: this.formData.date.format("YYYY-MM-DD"),
          project_id: this.formData.projectId,
          issue_id: this.formData.issueId,
          issue_summary: this.issueSummary || "",
          attribute_id: this.formData.attributeId,
          spent_time: this.formData.spentTime,
          note: this.formData.note,
          // When creating from timeline, pass employee_id from parent row if provided
          ...(this.logworkId ? {} : { employee_id: this.employeeId || undefined })
        };

        const request = this.logworkId
          ? service.updateLogwork(this.logworkId, payload)
          : service.createLogwork(payload);

        request
          .then(() => {
            this.$message.success(
              this.logworkId
                ? this.$t("logwork.modal.update_success") ||
                    "Logwork updated successfully"
                : this.$t("logwork.modal.log_success")
            );
            this.$emit("success");

            if (this.logAnother && !this.logworkId) {
              this.resetForm();
            } else {
              this.close();
            }
          })
          .catch(error => {
            console.error("Save logwork failed:", error);
            const errorMessage = error.response?.data?.message || this.$t("logwork.modal.log_error");
            this.$message.error(errorMessage);
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    handleCancel() {
      this.close();
    },
    resetForm() {
      this.form.resetFields();
      this.formData = {
        date: moment(),
        projectId: this.projects.length > 0 ? this.projects[0].id : null,
        issueId: null,
        attributeId: null,
        spentTime: null,
        note: ""
      };
      this.issueSummary = "";
      this.logAnother = true;
      this.$nextTick(() => {
        this.form.setFieldsValue({
          date: this.formData.date,
          projectId: this.formData.projectId,
          issueId: undefined,
          attributeId: undefined,
          spentTime: undefined,
          note: ""
        });
      });
    },
    close() {
      this.modalVisible = false;
      this.$emit("update:visible", false);
    }
  }
};
</script>

<style scoped>
.logwork-form {
  padding: 12px 0;
}

.required-star {
  color: #ff4d4f;
}

.time-input-group {
  position: relative;
}

.unit-label {
  margin-left: 8px;
  color: #666;
  font-size: 12px;
}

.help-text {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.issue-summary {
  margin-top: 5px;
  font-size: 12px;
  color: #1890ff;
  font-weight: 500;
}
.ant-form-item {
  margin-bottom: 8px;
}
::v-deep label {
  margin-bottom: 0px !important;
}
::v-deep p {
  margin-bottom: 0px !important;
}
::v-deep .ant-modal-body {
  padding: 0px 16px;
}
</style>
